import React, { createContext, ReactNode, useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Snackbar, SnackbarContainer } from 'Shared/components/design/snackbar/style';

interface SnackbarProviderProps {
  children       : ReactNode
  Container     ?: React.ElementType | React.ComponentType
  justifyContent?: string
}

interface SnackbarContextProps {
  enqueueSnackbar: (message: string, { duration }: { duration?: number }) => void;
}

const FADE_DURATION = 300;

export const SnackbarContext = createContext<SnackbarContextProps | undefined>(undefined);

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
  justifyContent,
  Container = React.Fragment
}) => {
  const visibilityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimer       = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [snackbar, setSnackbar] = useState<{ message: string; duration: number } | null>(null);
  const [fadeOut, setFadeOut]   = useState(false);

  const enqueueSnackbar = useCallback((message: string, { duration = 3000 }) => {
    setSnackbar({ message, duration });
    setFadeOut(false);

    if (fadeTimer.current) {
      clearTimeout(fadeTimer.current);
    }

    if (visibilityTimer.current) {
      clearTimeout(visibilityTimer.current);
    }

    fadeTimer.current = setTimeout(() => {
      setFadeOut(true);
      visibilityTimer.current = setTimeout(() => {
        setSnackbar(null);
      }, FADE_DURATION);
    }, duration);
  }, []);

  const snackbarNode = snackbar && (
    ReactDOM.createPortal(
      <SnackbarContainer justifyContent={justifyContent}>
        <Container>
          <Snackbar fadeOut={fadeOut}>{snackbar.message}</Snackbar>
        </Container>
      </SnackbarContainer>,
      document.body
    )
  );

  return (
    <SnackbarContext.Provider value={{ enqueueSnackbar }}>
      {children}
      {snackbarNode}
    </SnackbarContext.Provider>
  );
};
